import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const BUSINESS_NUMBER_SOURCE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{11,127}$/u;
const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;
const LOWER_48_BITS = 0xffffffffffffn;

export function isBusinessNumberSourceId(value: unknown): value is string {
  return (
    typeof value === "string" &&
    BUSINESS_NUMBER_SOURCE_PATTERN.test(value) &&
    [...value].filter((character) => /[A-Za-z0-9]/u.test(character)).length >= 12
  );
}

export const PurchasePayableReconciliationNumberSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^DZ-[0-9]{8}-[0-9A-F]{12}$",
});
export type PurchasePayableReconciliationNumber = Static<
  typeof PurchasePayableReconciliationNumberSchema
>;

export const SupplierPaymentNumberSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^FK-[0-9]{8}-[0-9A-F]{12}$",
});
export type SupplierPaymentNumber = Static<typeof SupplierPaymentNumberSchema>;

export const SupplierCreditNumberSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^DX-[0-9]{8}-[0-9A-F]{12}$",
});
export type SupplierCreditNumber = Static<typeof SupplierCreditNumberSchema>;

export const SupplierRefundNumberSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^GT-[0-9]{8}-[0-9A-F]{12}$",
});
export type SupplierRefundNumber = Static<typeof SupplierRefundNumberSchema>;

export const SupplierPaymentReversalNumberSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^CX-[0-9]{8}-[0-9A-F]{12}$",
});
export type SupplierPaymentReversalNumber = Static<typeof SupplierPaymentReversalNumberSchema>;

function isRealBusinessDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString() === `${value}T00:00:00.000Z`
  );
}

function deriveFortyEightBitHex(value: string): string {
  let hash = FNV_1A_64_OFFSET;
  for (const character of value) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  return (hash & LOWER_48_BITS).toString(16).toUpperCase().padStart(12, "0");
}

function deriveNumber(
  prefix: "DZ" | "FK" | "DX" | "GT" | "CX",
  businessDate: string,
  sourceId: string,
):
  | PurchasePayableReconciliationNumber
  | SupplierPaymentNumber
  | SupplierCreditNumber
  | SupplierRefundNumber
  | SupplierPaymentReversalNumber {
  if (!isRealBusinessDate(businessDate)) {
    throw new TypeError("businessDate must be a real calendar date in YYYY-MM-DD format");
  }
  if (!isBusinessNumberSourceId(sourceId)) {
    throw new TypeError("sourceId must contain at least 12 safe alphanumeric characters");
  }
  return `${prefix}-${businessDate.replaceAll("-", "")}-${deriveFortyEightBitHex(
    `${prefix}\0${businessDate}\0${sourceId}`,
  )}`;
}

export function derivePurchasePayableReconciliationNumber(
  businessDate: string,
  reconciliationId: string,
): PurchasePayableReconciliationNumber {
  return deriveNumber("DZ", businessDate, reconciliationId) as PurchasePayableReconciliationNumber;
}

export function deriveSupplierPaymentNumber(
  businessDate: string,
  paymentId: string,
): SupplierPaymentNumber {
  return deriveNumber("FK", businessDate, paymentId) as SupplierPaymentNumber;
}

export function deriveSupplierCreditNumber(
  businessDate: string,
  creditId: string,
): SupplierCreditNumber {
  return deriveNumber("DX", businessDate, creditId) as SupplierCreditNumber;
}

export function deriveSupplierRefundNumber(
  businessDate: string,
  refundId: string,
): SupplierRefundNumber {
  return deriveNumber("GT", businessDate, refundId) as SupplierRefundNumber;
}

export function deriveSupplierPaymentReversalNumber(
  businessDate: string,
  reversalId: string,
): SupplierPaymentReversalNumber {
  return deriveNumber("CX", businessDate, reversalId) as SupplierPaymentReversalNumber;
}

export function isPurchasePayableReconciliationNumber(
  value: unknown,
): value is PurchasePayableReconciliationNumber {
  if (!Value.Check(PurchasePayableReconciliationNumberSchema, value)) return false;
  const match = /^DZ-(\d{4})(\d{2})(\d{2})-[0-9A-F]{12}$/u.exec(value);
  return Boolean(match && isRealBusinessDate(`${match[1]}-${match[2]}-${match[3]}`));
}

export function isSupplierPaymentNumber(value: unknown): value is SupplierPaymentNumber {
  if (!Value.Check(SupplierPaymentNumberSchema, value)) return false;
  const match = /^FK-(\d{4})(\d{2})(\d{2})-[0-9A-F]{12}$/u.exec(value);
  return Boolean(match && isRealBusinessDate(`${match[1]}-${match[2]}-${match[3]}`));
}

export function isSupplierCreditNumber(value: unknown): value is SupplierCreditNumber {
  if (!Value.Check(SupplierCreditNumberSchema, value)) return false;
  const match = /^DX-(\d{4})(\d{2})(\d{2})-[0-9A-F]{12}$/u.exec(value);
  return Boolean(match && isRealBusinessDate(`${match[1]}-${match[2]}-${match[3]}`));
}

export function isSupplierRefundNumber(value: unknown): value is SupplierRefundNumber {
  if (!Value.Check(SupplierRefundNumberSchema, value)) return false;
  const match = /^GT-(\d{4})(\d{2})(\d{2})-[0-9A-F]{12}$/u.exec(value);
  return Boolean(match && isRealBusinessDate(`${match[1]}-${match[2]}-${match[3]}`));
}

export function isSupplierPaymentReversalNumber(
  value: unknown,
): value is SupplierPaymentReversalNumber {
  if (!Value.Check(SupplierPaymentReversalNumberSchema, value)) return false;
  const match = /^CX-(\d{4})(\d{2})(\d{2})-[0-9A-F]{12}$/u.exec(value);
  return Boolean(match && isRealBusinessDate(`${match[1]}-${match[2]}-${match[3]}`));
}
